import {
  ApplicationConfig,
  Type,
  LOCALE_ID,
  Provider,
  EnvironmentProviders,
} from "@angular/core";
import { loadTranslations } from "@angular/localize";
import { AvailableLocales, LOCALE } from "shared";
import { bootstrapApplication } from "@angular/platform-browser";
import moment from "moment";
import { registerLocaleData } from "@angular/common";

// Import locale data
import localeEn from "@angular/common/locales/en";
import localeDe from "@angular/common/locales/de";
import localeFr from "@angular/common/locales/fr";
import localeIt from "@angular/common/locales/it";
import localeEs from "@angular/common/locales/es";
import localeUk from "@angular/common/locales/uk";
import localeNl from "@angular/common/locales/nl";
import localeHu from "@angular/common/locales/hu";
import localeCs from "@angular/common/locales/cs";

// Import extra locale data
import localeEnExtra from "@angular/common/locales/extra/en";
import localeDeExtra from "@angular/common/locales/extra/de";
import localeFrExtra from "@angular/common/locales/extra/fr";
import localeItExtra from "@angular/common/locales/extra/it";
import localeEsExtra from "@angular/common/locales/extra/es";
import localeUkExtra from "@angular/common/locales/extra/uk";
import localeNlExtra from "@angular/common/locales/extra/nl";
import localeHuExtra from "@angular/common/locales/extra/hu";
import localeCsExtra from "@angular/common/locales/extra/cs";

// Register locale data
registerLocaleData(localeEn, "en-GB", localeEnExtra);
registerLocaleData(localeDe, "de-DE", localeDeExtra);
registerLocaleData(localeFr, "fr-FR", localeFrExtra);
registerLocaleData(localeIt, "it-IT", localeItExtra);
registerLocaleData(localeEs, "es-ES", localeEsExtra);
registerLocaleData(localeUk, "uk-UA", localeUkExtra);
registerLocaleData(localeNl, "nl-NL", localeNlExtra);
registerLocaleData(localeHu, "hu-HU", localeHuExtra);
registerLocaleData(localeCs, "cs-CZ", localeCsExtra);

// Mapping between language codes and full locales
const dbToLanguageAndRegionLocale: Record<string, string> = {
  en: "en-GB",
  de: "de-DE",
  fr: "fr-FR",
  it: "it-IT",
  es: "es-ES",
  uk: "uk-UA",
  nl: "nl-NL",
  hu: "hu-HU",
  cs: "cs-CZ",
};

const availableLocales = Object.values(AvailableLocales).filter(
  (value) => typeof value === "string",
) as string[];

function getLocale(localeString: string | null): AvailableLocales {
  return availableLocales.includes(localeString || "[NONE]")
    ? (localeString as AvailableLocales)
    : AvailableLocales.English;
}

// Update the interface to match the actual file structure
type TranslationsObject = Record<string, string>;

function fetchLocale(
  locale: AvailableLocales,
): Promise<TranslationsObject | null> {
  const path = `/assets/locales/messages.${locale}.json`;

  return fetch(path)
    .then((res) => {
      if (!res.ok) {
        throw new Error(
          `Failed to fetch locale: ${res.status} ${res.statusText}`,
        );
      }
      return res.json() as Promise<TranslationsObject>;
    })
    .catch((err) => {
      console.error(`Error loading translations for locale "${locale}":`, err);
      return null;
    });
}

export function bootstrapWithLanguage(
  appComponent: Type<unknown>,
  forceLocale: string | null = null,
  providers: Array<Provider | EnvironmentProviders> = [],
): Promise<unknown> {
  const validForceLocale =
    forceLocale !== null && availableLocales.includes(forceLocale);
  const localeCurrent = localStorage.getItem(LOCALE) || null;
  const localeValue = validForceLocale ? forceLocale : localeCurrent;
  const locale = getLocale(localeValue) as AvailableLocales;
  moment.locale(locale);

  if (localeValue !== localeCurrent) {
    localStorage.setItem(LOCALE, locale);
  }

  return fetchLocale(locale)
    .then((translations) => {
      if (translations) {
        // Load the translations directly since they're already in the correct format
        loadTranslations(translations);
        console.log(`Loaded translations for locale: ${locale}`);
      } else {
        console.warn(`No translations found for locale: ${locale}`);
      }

      const localeWithRegion = dbToLanguageAndRegionLocale[locale];

      // Add locale providers
      const localeProviders: Provider[] = [
        { provide: LOCALE_ID, useValue: localeWithRegion },
      ];

      const config: ApplicationConfig = {
        providers: [...providers, ...localeProviders],
      };

      return bootstrapApplication(appComponent, config);
    })
    .catch((err: Error) => {
      console.error("Failed to bootstrap with language:", err);
      throw err;
    });
}
